import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEstimationStore } from '../../stores/estimationStore';
import { useAuthStore } from '../../stores/authStore';
import { useUIStore } from '../../stores/uiStore';
import { useTenantStore } from '../../stores/tenantStore';
import { getEnabledModules } from '../../modules';
import {
  HiOutlineHome,
  HiOutlineFolderOpen,
  HiOutlineCalculator,
  HiOutlineUserGroup,
  HiOutlineClipboardDocumentList,
  HiOutlineChartBarSquare,
  HiOutlineBanknotes,
  HiOutlineClock,
  HiOutlineUsers,
  HiOutlineArrowDownTray,
  HiOutlineBuildingLibrary,
  HiOutlineUserCircle,
  HiOutlineBeaker,
  HiOutlineViewColumns,
  HiOutlineClipboardDocumentCheck,
  HiOutlineChatBubbleLeftRight,
  HiOutlinePuzzlePiece,
  HiOutlineChevronDown,
} from 'react-icons/hi2';
import type { IconType } from 'react-icons';
import styles from './Sidebar.module.css';

interface NavItem {
  key: string;
  label: string;
  icon: IconType;
  path: string;
  matchFn?: (pathname: string) => boolean;
}

interface NavGroup {
  key: string;
  label: string;
  items: NavItem[];
  adminOnly?: boolean;
}

type TabDef = { key: string; icon: IconType; ns: string; label?: string; labelKey?: string };

// Mirrors v11 planificateur navigation exactly. Niveau 1 = 4 basic tabs, Niveau 2 = 11 advanced tabs.
const TABS_N1: TabDef[] = [
  { key: 'estimation', icon: HiOutlineCalculator, ns: 'estimation' },
  { key: 'demographics', icon: HiOutlineUserGroup, ns: 'demographics' },
  { key: 'plq', icon: HiOutlineClipboardDocumentList, ns: 'plq' },
  { key: 'ratios', icon: HiOutlineChartBarSquare, ns: 'common', labelKey: 'tabRatiosSources' },
];

const TABS_N2: TabDef[] = [
  { key: 'results_n2', icon: HiOutlineChartBarSquare, ns: 'common', labelKey: 'tabResultsN2' },
  { key: 'parameters', icon: HiOutlineBeaker, ns: 'common', labelKey: 'tabParameters' },
  { key: 'commune', icon: HiOutlineBuildingLibrary, ns: 'common', labelKey: 'tabCommune' },
  { key: 'programme', icon: HiOutlineViewColumns, ns: 'common', labelKey: 'tabProgrammeDetail' },
  { key: 'subsidies', icon: HiOutlineBanknotes, ns: 'subsidies' },
  { key: 'amortization', icon: HiOutlineClock, ns: 'amortization' },
  { key: 'planning', icon: HiOutlineClock, ns: 'planning' },
  { key: 'population', icon: HiOutlineUsers, ns: 'population' },
  { key: 'finances', icon: HiOutlineBanknotes, ns: 'finances' },
  { key: 'municipal', icon: HiOutlineBuildingLibrary, ns: 'finances', labelKey: 'municipalTabName' },
  { key: 'export', icon: HiOutlineArrowDownTray, ns: 'export' },
];

function buildNavGroups(): NavGroup[] {
  return [
    {
      key: 'Core',
      label: 'Core',
      items: [
        { key: 'dashboard', label: 'Dashboard', icon: HiOutlineHome, path: '/app', matchFn: (p) => p === '/app' },
        { key: 'projects', label: 'Projects', icon: HiOutlineFolderOpen, path: '/app/projects', matchFn: (p) => p === '/app/projects' },
        { key: 'reference', label: 'Reference', icon: HiOutlineBuildingLibrary, path: '/app/reference-projects', matchFn: (p) => p.startsWith('/app/reference-projects') },
      ],
    },
    {
      key: 'Management',
      label: 'Management',
      items: [
        { key: 'workflow', label: 'Workflow', icon: HiOutlineViewColumns, path: '/app/workflow', matchFn: (p) => p === '/app/workflow' },
        { key: 'actuals', label: 'Actuals', icon: HiOutlineClipboardDocumentCheck, path: '/app/actuals', matchFn: (p) => p === '/app/actuals' },
        { key: 'consultation', label: 'Consult', icon: HiOutlineChatBubbleLeftRight, path: '/app/consultation', matchFn: (p) => p === '/app/consultation' },
        { key: 'optimizer', label: 'Optimizer', icon: HiOutlinePuzzlePiece, path: '/app/optimizer', matchFn: (p) => p === '/app/optimizer' },
      ],
    },
    {
      key: 'Tools',
      label: 'Tools',
      items: [
        { key: 'activity', label: 'Activity', icon: HiOutlineClock, path: '/app/activity', matchFn: (p) => p === '/app/activity' },
        { key: 'settings', label: 'Settings', icon: HiOutlineUserCircle, path: '/app/settings', matchFn: (p) => p === '/app/settings' },
      ],
    },
    {
      key: 'Admin',
      label: 'Admin',
      adminOnly: true,
      items: [
        { key: 'admin', label: 'Users & Invitations', icon: HiOutlineUsers, path: '/app/admin', matchFn: (p) => p === '/app/admin' },
        { key: 'import', label: 'Import', icon: HiOutlineArrowDownTray, path: '/app/import', matchFn: (p) => p === '/app/import' },
      ],
    },
  ];
}

export function Sidebar() {
  const { t } = useTranslation(['common', 'nav']);
  const navigate = useNavigate();
  const location = useLocation();
  const activeTab = useEstimationStore((s) => s.activeTab);
  const setActiveTab = useEstimationStore((s) => s.setActiveTab);
  const niveau = useEstimationStore((s) => s.niveau);
  const ha = useEstimationStore((s) => s.ha);
  const setHa = useEstimationStore((s) => s.setHa);
  const haZi = useEstimationStore((s) => s.haZi);
  const setHaZi = useEstimationStore((s) => s.setHaZi);
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const expandedGroups = useUIStore((s) => s.expandedGroups);
  const toggleGroup = useUIStore((s) => s.toggleGroup);
  const features = useTenantStore((s) => s.features);

  const isProjectDetail = /^\/app\/projects\/[^/]+/.test(location.pathname) && location.pathname !== '/app/projects';
  const isAdmin = role === 'tenant_admin' || role === 'super_admin';
  const navGroups = buildNavGroups();

  // Inject CIVITAS module nav groups
  const enabledModules = getEnabledModules(features as unknown as Record<string, boolean>);
  const moduleNavGroups: NavGroup[] = enabledModules.map((mod) => ({
    key: mod.name,
    label: mod.label,
    items: mod.navItems.map((item) => ({
      key: item.key,
      label: item.label,
      icon: item.icon,
      path: item.path,
      matchFn: item.matchFn,
    })),
  }));

  // Insert module groups before Admin group
  const adminIdx = navGroups.findIndex((g) => g.key === 'Admin');
  const allNavGroups = adminIdx >= 0
    ? [...navGroups.slice(0, adminIdx), ...moduleNavGroups, ...navGroups.slice(adminIdx)]
    : [...navGroups, ...moduleNavGroups];

  const mainNav = (
    <nav className={styles.nav} style={isProjectDetail ? { flex: 'none' } : undefined}>
      {allNavGroups.map((group) => {
        if (group.adminOnly && !isAdmin) return null;
        const isExpanded = expandedGroups[group.key] !== false;
        return (
          <div key={group.key} className={styles.navGroup}>
            <button
              className={styles.groupHeader}
              onClick={() => toggleGroup(group.key)}
            >
              <span className={styles.groupLabel}>{t(`nav:${group.key.toLowerCase()}`, group.label)}</span>
              <span className={`${styles.groupChevron} ${isExpanded ? styles.groupChevronOpen : ''}`}>
                <HiOutlineChevronDown />
              </span>
            </button>
            {isExpanded && (
              <div className={styles.groupItems}>
                {group.items.map((item) => {
                  const isActive = item.matchFn
                    ? item.matchFn(location.pathname)
                    : location.pathname === item.path;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      className={`${styles.tabBtn} ${isActive ? styles.activeTab : ''}`}
                      onClick={() => navigate(item.path)}
                    >
                      <span className={styles.tabIcon}><Icon /></span>
                      <span className={styles.tabLabel}>{t(`nav:${item.key}`, item.label)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );

  return (
    <aside className={styles.sidebar}>
      {isProjectDetail ? (
        <>
          {/* Back link */}
          <button
            className={styles.backLink}
            onClick={() => navigate('/app/projects')}
          >
            &larr; {t('nav:projects', 'Projects')}
          </button>

          {/* Project-specific tabs — shown first for prominence */}
          <nav className={styles.projectNav}>
            {(niveau === 1 ? TABS_N1 : TABS_N2).map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  className={`${styles.tabBtn} ${activeTab === tab.key ? styles.activeTab : ''}`}
                  onClick={() => setActiveTab(tab.key)}
                >
                  <span className={styles.tabIcon}><Icon /></span>
                  <span className={styles.tabLabel}>{tab.label ?? t(`${tab.ns}:${tab.labelKey ?? 'tabName'}`, tab.key)}</span>
                </button>
              );
            })}
          </nav>

          {/* Sliders */}
          <div className={styles.sliders}>
            <label className={styles.sliderLabel}>
              <span>{t('common:hectares', 'Surface du périmètre du projet')}</span>
              <span className={styles.sliderValue}>{ha} ha</span>
            </label>
            <input
              type="range"
              min={10}
              max={100}
              step={1}
              value={ha}
              onChange={(e) => setHa(Number(e.target.value))}
              className={styles.slider}
            />
            <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
              {t('common:programmeMixte', 'Programme mixte')} · {Math.max(0, ha - haZi)} ha
            </div>

            <label className={styles.sliderLabel}>
              <span>{t('common:hectaresZI', 'Surface du périmètre de la ZI')}</span>
              <span className={styles.sliderValue}>{haZi} ha</span>
            </label>
            <input
              type="range"
              min={0}
              max={Math.min(50, ha)}
              step={1}
              value={haZi}
              onChange={(e) => setHaZi(Math.min(Number(e.target.value), ha))}
              className={styles.slider}
            />
            {haZi === 0 && (
              <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>
                {t('common:noZIHint', 'Aucune ZI — glisser pour activer')}
              </div>
            )}
          </div>

          {/* Main navigation — compact below */}
          {mainNav}
        </>
      ) : (
        mainNav
      )}
    </aside>
  );
}
